import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { CacheHandlerService } from 'src/app/kuba/cached.services';
import { environment } from 'src/environments/environment';

interface PortalBusinessRow {
  businessId: number;
  businessName: string;
  percentage: number;
  smiley: 'red' | 'yellow' | 'green';
  hseAdvisorName?: string | null;
  completedOn: string | null;
}

interface PortalSummary {
  totalCount: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  businesses: PortalBusinessRow[];
}

/**
 * Portal-dashboard widget — pie chart of BHT-samsvar completion across
 * every business under the portal manager's portal. Three slices
 * (red / yellow / green) match the existing smiley pack. The supporting
 * list under the chart shows businesses per category; clicking a row
 * pops the business's Flexi tab in a new window via the same R-mode
 * shim used by the activities-portal-widget.
 *
 * Visibility: the dashboard renders this widget only for portals with Flexi
 * enabled. Those portals get a neutral empty state when they have no completed
 * assessments yet.
 */
@Component({
  selector: 'app-flexi-compliance-portal-widget',
  templateUrl: './flexi-compliance-portal-widget.component.html',
  styleUrls: ['./flexi-compliance-portal-widget.component.scss'],
  standalone: false
})
export class FlexiCompliancePortalWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  loading = true;
  summary: PortalSummary | null = null;

  /** Chart input — recomputed whenever the summary or active filter changes. */
  chartData: { name: string; value: number }[] = [];
  /**
   * Pinned colors keyed by slice name. ngx-charts' `colorScheme.domain`
   * maps by data-index, so if we filter out the green slice the yellow
   * slice would inherit the green color. `customColors` binds by name,
   * keeping traffic-light hues correct regardless of which buckets are
   * populated.
   */
  customColors: { name: string; value: string }[] = [];
  /** Click a slice to narrow the supporting list to just that bucket. Null = all. */
  activeFilter: 'red' | 'yellow' | 'green' | null = null;

  /**
   * ngx-charts requires explicit `[width, height]`; it won't reflow on
   * container resize on its own. We track the host cell and update `view`
   * via a ResizeObserver so the donut always fits the grid column.
   */
  @ViewChild('chartHost', { static: false }) chartHost?: ElementRef<HTMLElement>;
  /**
   * Donut size is intentionally small and roughly square — the chips above
   * (Solid / Delvis / Mangler counts) carry the same data the legend would,
   * so we hide the legend and use the donut as a quick-glance visualisation.
   * Keeping the height tight stops the widget from overflowing its
   * dashboard cell on shorter screens.
   */
  view: [number, number] = [120, 120];
  gradient = false;
  showLegend = false;
  showLabels = false;
  isDoughnut = true;
  legendPosition: 'right' | 'below' = 'below';
  legendTitle = '';
  /** Hard cap — the dashboard cell only gives us 226px content height. */
  private readonly chartHeight = 120;
  private resizeObserver?: ResizeObserver;

  // Order matches the chart slices (Green, Yellow, Red) and the color
  // scheme below — the smiley bucket is the canonical sort.
  readonly colorScheme = {
    domain: ['#15803d', '#ca8a04', '#dc2626']
  };

  /** Paginated supporting list. */
  paginatedRows: PortalBusinessRow[] = [];
  currentPage = 1;
  itemsPerPage = 6;
  totalPages = 0;

  private remoteWindow: Window | null = null;

  constructor(
    private http: HttpClient,
    private cacheHandlerService: CacheHandlerService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.legendTitle = this.translate.instant('FLEXI_COMPLIANCE.PORTAL_WIDGET.LEGEND_TITLE');
    const url = `${environment.BASE_URL}/flexi/compliance/portal/${BaseServices.UserId}/summary`;
    // The card soft-fails to an empty state below, so the generic error toast is
    // suppressed rather than shown on whichever page the response arrives on (#6021).
    this.http.get<any>(url, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true)
    }).subscribe({
      next: data => {
        this.summary = this.normalizeSummary(data);
        this.rebuildChart();
        this.refreshPagination();
        this.loading = false;
      },
      error: () => {
        // Soft-fail: render the empty state instead of crashing the
        // dashboard. Portal users often have permissions that vary.
        this.summary = this.emptySummary();
        this.refreshPagination();
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined' || !this.chartHost) return;
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (width > 0) this.zone.run(() => this.applyChartSize(width));
      });
      this.resizeObserver.observe(this.chartHost!.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /**
   * Cap the donut at a compact square (≤ chartHeight on the short side) and
   * never let it claim more than the host cell can give. We don't scale up
   * with the cell because the chips above already convey the count; a huge
   * donut would just push the supporting list off-screen.
   */
  private applyChartSize(rawWidth: number): void {
    const available = Math.max(120, Math.floor(rawWidth) - 2);
    const side = Math.min(available, this.chartHeight);
    const next: [number, number] = [side, this.chartHeight];
    if (next[0] !== this.view[0] || next[1] !== this.view[1]) {
      this.view = next;
      this.cdr.markForCheck();
    }
  }

  /**
   * Case-tolerant payload mapper (server may emit PascalCase) with hard
   * defaults — an empty/204 response must never leave `businesses`
   * undefined, or every downstream `.length` access crashes the dashboard.
   */
  private normalizeSummary(r: any): PortalSummary {
    if (!r) return this.emptySummary();
    const rows = (r.businesses ?? r.Businesses ?? []) as any[];
    return {
      totalCount: r.totalCount ?? r.TotalCount ?? rows.length ?? 0,
      greenCount: r.greenCount ?? r.GreenCount ?? 0,
      yellowCount: r.yellowCount ?? r.YellowCount ?? 0,
      redCount: r.redCount ?? r.RedCount ?? 0,
      businesses: rows.map(b => ({
        businessId: b.businessId ?? b.BusinessId,
        businessName: b.businessName ?? b.BusinessName ?? '',
        percentage: Number(b.percentage ?? b.Percentage ?? 0),
        smiley: (b.smiley ?? b.Smiley ?? 'red') as 'red' | 'yellow' | 'green',
        hseAdvisorName: b.hseAdvisorName ?? b.HseAdvisorName ?? null,
        completedOn: b.completedOn ?? b.CompletedOn ?? null
      }))
    };
  }

  // ---------- Derived ----------

  get filteredRows(): PortalBusinessRow[] {
    const rows = this.summary?.businesses ?? [];
    if (this.activeFilter === null) return rows;
    return rows.filter(b => b.smiley === this.activeFilter);
  }

  private rebuildChart(): void {
    if (!this.summary) {
      this.chartData = [];
      this.customColors = [];
      return;
    }
    // Suppress zero-count slices to avoid an ugly empty wedge — but always
    // keep at least one slice so ngx-charts doesn't error on empty data.
    const all = [
      { name: this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL'), value: this.summary.greenCount, color: '#15803d', key: 'green' as const },
      { name: this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL'), value: this.summary.yellowCount, color: '#ca8a04', key: 'yellow' as const },
      { name: this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL'), value: this.summary.redCount, color: '#dc2626', key: 'red' as const }
    ];
    const visible = all.filter(s => s.value > 0);
    this.chartData = visible.map(({ name, value }) => ({ name, value }));
    // Pin colors by slice name so green/yellow/red always render in the
    // correct traffic-light hue, even when some buckets are empty.
    this.customColors = visible.map(({ name, color }) => ({ name, value: color }));
  }

  // ---------- Filter ----------

  setFilter(slice: 'red' | 'yellow' | 'green' | null): void {
    this.activeFilter = this.activeFilter === slice ? null : slice;
    this.currentPage = 1;
    this.refreshPagination();
  }

  /**
   * Clicking a slice opens the full-page business list pre-filtered to
   * that bucket. ngx-charts emits the slice's localized name as
   * <c>event.name</c> — map it back to the smiley key via the same
   * translations used in rebuildChart() so the handler is locale-safe.
   */
  onSliceSelect(event: any): void {
    const name = event?.name ?? event;
    const greenName = this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL');
    const yellowName = this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL');
    const redName = this.translate.instant('FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL');
    let bucket: 'red' | 'yellow' | 'green' | null = null;
    if (name === greenName) bucket = 'green';
    else if (name === yellowName) bucket = 'yellow';
    else if (name === redName) bucket = 'red';
    this.goToList(bucket);
  }

  /** Full-page business list (dashboard/flexi-compliance), optionally pre-filtered. */
  goToList(filter: 'red' | 'yellow' | 'green' | null = null): void {
    this.router.navigate(['flexi-compliance'], {
      relativeTo: this.route,
      queryParams: filter ? { filter } : {}
    });
  }

  // ---------- Pagination ----------

  refreshPagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredRows.length / this.itemsPerPage));
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedRows = this.filteredRows.slice(start, start + this.itemsPerPage);
  }
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.refreshPagination();
  }

  // ---------- Drilldown ----------

  /**
   * Opens the business's Flexi tab in a new window using the same R-mode
   * sessionStorage shim as <c>activities-portal-widget</c>. Single re-used
   * window — clicking another row reuses the same browser window.
   */
  openBusiness(row: PortalBusinessRow): void {
    if (this.remoteWindow) this.remoteWindow.close();
    this.cacheHandlerService.clearCachedData();
    sessionStorage.setItem('RModeBusiness', JSON.stringify({ BusinessName: row.businessName }));
    sessionStorage.setItem('RModeBusinessId', row.businessId.toString());

    const baseUrl = BaseServices.userBase?.BaseUrl ?? '';
    const url = `${window.location.origin}/${baseUrl}/app/business/${row.businessId}/Flexi/compliance`;
    const features = `location=yes,width=${screen.availWidth},height=${screen.availHeight},scrollbars=yes,status=yes`;
    this.remoteWindow = window.open(url, 'Remote', features);
  }

  // ---------- View helpers ----------

  smileyClass(smiley: string): string {
    return `is-${smiley}`;
  }

  smileyLabelKey(smiley: string): string {
    switch (smiley) {
      case 'green': return 'FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL';
      case 'yellow': return 'FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL';
      default: return 'FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL';
    }
  }

  private emptySummary(): PortalSummary {
    return { totalCount: 0, greenCount: 0, yellowCount: 0, redCount: 0, businesses: [] };
  }
}
